import {
  AgreementTypeEnum,
  AgreementsApi,
  Configuration,
} from '@sr-sdks/api-sdk-axios';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

// ===== API CLIENT =====

/**
 * Create an agreements API instance
 */
const getAgreementsApi = () => {
  return new AgreementsApi(
    new Configuration(),
    configuration.SERVICES_API_BASE,
    axiosInstance,
  );
};

// ===== QUERY KEYS =====

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const agreementsKeys = {
  all: ['agreements'] as const,
  paginated: (params: AgreementQueryParams) =>
    [...agreementsKeys.all, 'paginated', { ...params }] as const,
};

// ===== API FUNCTIONS =====

export interface AgreementQueryParams {
  agreementType?: AgreementTypeEnum;
  beneficiaryTitleId?: string;
  page?: number;
  pageSize?: number;
  serviceId?: string;
  sortBy?: string;
}

/**
 * Default parameters for Agreements queries. Merged into
 * {@link queryOptionsGetAgreements} before the query key is built, so an
 * unset page/pageSize/sortBy never creates a distinct cache entry.
 */
export const AGREEMENTS_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 50,
  sortBy: 'agreementDate:desc',
} as const satisfies AgreementQueryParams;

/**
 * {@link fetchAgreements} always has a real `serviceId` by the time it runs —
 * {@link queryOptionsGetAgreements} only enables the query once one is
 * present — so it's typed as required here rather than falling back to an
 * empty string, which the backend's `@IsUUID()` validation rejects anyway.
 */
type FetchAgreementsParams = Omit<AgreementQueryParams, 'serviceId'> & {
  serviceId: string;
};

/**
 * Fetch agreements with optional filters and pagination
 */
export const fetchAgreements = async (params: FetchAgreementsParams) => {
  try {
    const {
      agreementType,
      beneficiaryTitleId,
      page,
      pageSize,
      serviceId,
      sortBy,
    } = params;

    const response = await getAgreementsApi().agreementControllerFindAll(
      serviceId,
      agreementType,
      beneficiaryTitleId,
      page,
      pageSize,
      sortBy,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'agreementControllerFindAll',
      params: params,
    });
  }
};

// ===== QUERY OPTIONS =====

/**
 * React Query options for a paginated agreements list.
 * Skips the fetch when `serviceId` is missing (caller opt-out).
 */
export const queryOptionsGetAgreements = (params?: AgreementQueryParams) => {
  const merged = { ...AGREEMENTS_DEFAULT_PARAMS, ...params };
  const { serviceId } = merged;

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- lint-backlog: queryKey omits a value the queryFn reads; adding it changes caching
  return queryOptions({
    queryFn: serviceId
      ? () => fetchAgreements({ ...merged, serviceId })
      : skipToken,
    queryKey: agreementsKeys.paginated(merged),
  });
};

// ===== QUERY HOOKS =====

/** Narrow opt-in flags for {@link useAgreements} call sites. */
export type UseAgreementsOptions = {
  enabled?: boolean;
  /** Opt in for pagination UX; not set on shared query options. */
  placeholderData?: typeof keepPreviousData;
};

/**
 * Hook for fetching agreements with optional filters.
 * `options` is intentionally narrow so call sites opt into behaviour like
 * `placeholderData: keepPreviousData` without affecting every consumer.
 */
export const useAgreements = (
  params?: AgreementQueryParams,
  options?: UseAgreementsOptions,
) => {
  return useQuery({
    ...queryOptionsGetAgreements(params),
    ...options,
  });
};
